import interceptor from '../common/interceptor';
import { APIPaths } from '../common/constants';
import { OperationType } from '@/common/enum';
import type { EmailDraftAI, EmailDraftUser } from './emailReplyContract';
import { normalizeDraftAIStatus } from './emailReplyContract';
import type { EmailReplyData } from './shipment';

/**
 * Calls of the reply-to-AI-emails contract (ZOO-696 <-> ZOO-698, v0.3,
 * section 2). Every call is for the new flow only, so none of them runs for a
 * company without the feature. Each shows its own failures (inline), so none
 * lets the shared interceptor toast.
 */

const repliesUrl = (alertId: string, path = ''): string =>
  `${APIPaths.alertEmailReplies}${alertId}/email-replies/${path}`;

// The envelope is { data: { data: { is_success, data: <payload> } } }, as in
// ApiResponse: axios' own `data`, then the response wrapper, then the payload.
type Envelope<T> = { data?: { data?: { data?: T } } };
const payloadOf = <T>(res: Envelope<T>): T | undefined => res?.data?.data?.data;

/** The AI draft only; polled while it is still being generated. */
export const getEmailDraftStatus = async (
  token: string,
  alertId: string,
  signal?: AbortSignal
): Promise<EmailDraftAI> => {
  const res = await interceptor.axiosGet(
    'Get',
    repliesUrl(alertId, 'draft-status/'),
    token,
    undefined,
    signal,
    true
  );
  const ai = payloadOf<{ ai?: Partial<EmailDraftAI> }>(res)?.ai;
  return {
    status: normalizeDraftAIStatus(ai?.status),
    body: ai?.body ?? null,
    generated_at: ai?.generated_at ?? null,
  };
};

/**
 * "Draft with AI" (contract v0.5): ask for the AI answer to the target
 * thread's latest email. The server returns a ready draft at once, `pending`
 * (generation started or already running), or `none` (nothing to answer).
 */
export const postDraftWithAi = async (
  token: string,
  alertId: string,
  threadId: string | null
): Promise<EmailDraftAI> => {
  const res = await interceptor.axiosPost(
    'Post',
    repliesUrl(alertId, 'draft-with-ai/'),
    threadId ? { thread_id: threadId } : {},
    token,
    null,
    OperationType.SilentWrite,
    true
  );
  const ai = payloadOf<Partial<EmailDraftAI>>(res);
  return {
    status: normalizeDraftAIStatus(ai?.status),
    body: ai?.body ?? null,
    generated_at: ai?.generated_at ?? null,
  };
};

/** Autosave of this user's draft. */
export const putEmailDraft = async (
  token: string,
  alertId: string,
  draft: { body: string; attachment_ids: string[] }
): Promise<EmailDraftUser | undefined> => {
  const res = await interceptor.axiosPost(
    'Put',
    repliesUrl(alertId, 'draft/'),
    draft,
    token,
    null,
    OperationType.SilentWrite,
    true
  );
  return payloadOf<EmailDraftUser>(res);
};

export const deleteEmailDraft = async (
  token: string,
  alertId: string
): Promise<void> => {
  await interceptor.axiosPost(
    'Delete',
    repliesUrl(alertId, 'draft/'),
    undefined,
    token,
    null,
    OperationType.SilentWrite,
    true
  );
};

/** The user opened the thread: clears their unread dot. */
export const postEmailThreadSeen = async (
  token: string,
  alertId: string
): Promise<void> => {
  await interceptor.axiosPost(
    'Post',
    repliesUrl(alertId, 'seen/'),
    undefined,
    token,
    null,
    OperationType.SilentWrite,
    true
  );
};

/** Let the AI auto-reply on this thread again (Case A, after a human took over). */
export const postResumeEmailAi = async (
  token: string,
  alertId: string
): Promise<{ paused: boolean }> => {
  const res = await interceptor.axiosPost(
    'Post',
    repliesUrl(alertId, 'resume-ai/'),
    undefined,
    token,
    null,
    OperationType.Write,
    true
  );
  return payloadOf<{ paused: boolean }>(res) ?? { paused: false };
};

export interface EmailReplyDocument {
  id: string;
  file_name: string;
  size_bytes: number;
  created_at: string;
}

/** Shipment documents the user may attach to a reply. */
export const getEmailReplyDocuments = async (
  token: string,
  alertId: string
): Promise<EmailReplyDocument[]> => {
  const res = await interceptor.axiosGet(
    'Get',
    repliesUrl(alertId, 'documents/'),
    token,
    undefined,
    null,
    true
  );
  return payloadOf<EmailReplyDocument[]>(res) ?? [];
};

export interface NativeReplyInput {
  body: string;
  /** Reused on Retry so a second try cannot send twice. */
  clientRequestId: string;
  files: File[];
  /** Shipment document ids. */
  attachmentIds: string[];
  lastSeenMessageId: string | null;
  threadId: string | null;
  /** How many [missing] / [not attached yet] were sent with Send anyway. */
  placeholdersSent: number;
  /** The box was pre-filled with the AI draft (metrics: replies that start from it). */
  draftedFromAi: boolean;
  /**
   * The AI text that was pre-filled, as the user saw it. With the final body it
   * gives the share of the draft that was changed, for the quality metric.
   */
  aiDraftText: string;
}

export const postNativeEmailReply = async (
  token: string,
  alertId: string,
  input: NativeReplyInput
): Promise<EmailReplyData> => {
  const form = new FormData();
  form.append('body', input.body);
  form.append('client_request_id', input.clientRequestId);
  input.attachmentIds.forEach((id) => form.append('attachment_ids', id));
  input.files.forEach((file) => form.append('files', file));
  if (input.lastSeenMessageId) {
    form.append('last_seen_message_id', input.lastSeenMessageId);
  }
  if (input.threadId) form.append('thread_id', input.threadId);
  if (input.placeholdersSent > 0) {
    form.append('placeholders_sent', String(input.placeholdersSent));
  }
  form.append('drafted_from_ai', String(input.draftedFromAi));
  if (input.draftedFromAi) form.append('ai_draft_text', input.aiDraftText);
  const res = await interceptor.axiosPostForUpload(
    'Post',
    repliesUrl(alertId),
    form,
    token,
    null,
    true
  );
  const reply = payloadOf<EmailReplyData>(res);
  if (!reply) throw new Error('Empty reply response');
  return reply;
};

export interface ReplyFailure {
  status: number | null;
  /** "newer_reply" when another user sent while this one was drafting. */
  code: string | null;
  repliedBy: string | null;
  message: string;
}

/** The error shape of ZOO-694: { error, status: "failed", code?, replied_by? }. */
export const toReplyFailure = (error: unknown): ReplyFailure => {
  const response =
    (error as {
      response?: { status?: number; data?: Record<string, unknown> };
      status?: number;
      data?: Record<string, unknown>;
    }) ?? {};
  // Some callers reject the axios response, others the axios error.
  const status = response.response?.status ?? response.status ?? null;
  const data = response.response?.data ?? response.data ?? {};
  return {
    status,
    code: typeof data.code === 'string' ? data.code : null,
    repliedBy: typeof data.replied_by === 'string' ? data.replied_by : null,
    message:
      typeof data.error === 'string' && data.error
        ? data.error
        : 'Couldn’t send',
  };
};
